import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Languages, Check } from "lucide-react";
import { SUPPORTED_LANGS, ensureHindiLoaded, LS_LANG_KEY } from "@/i18n";
import { API_BASE } from "@/lib/api";

const LABELS = { en: "English", hi: "हिन्दी" };
// R109.33 — marker in localStorage recording an explicit user pick. Once set,
// AuthContext.syncLangFromProfile stops auto-overriding on bootstrap so a
// stale/lagging server profile can never silently flip the language back.
const LS_LANG_USER_SET = "venturekhoj_lang_user_set";

/**
 * LanguageSwitcher — dropdown in navbar.
 *   Persists to i18next (localStorage) AND to user profile (`language` field)
 *   when the user is signed in — so their preference survives login on other devices.
 */
export function LanguageSwitcher({ variant = "dark" }) {
  const { i18n } = useTranslation();
  const { user, refresh } = useAuth();
  // R109.34 — mirror i18n's current language into local React state and
  // explicitly subscribe to `languageChanged`. `useTranslation()` only forces
  // a re-render when `t` is consumed inside this component; here we only read
  // `i18n` for the dropdown label, so without an explicit subscription the
  // label can lag behind the actual language (visible bug: label reads
  // "English" while the rest of the page renders Hindi after a profile-driven
  // language switch).
  //
  // Prefer `i18n.language` over `resolvedLanguage`: when Hindi is the target
  // but the lazy bundle hasn't landed yet, `resolvedLanguage` falls back to
  // "en" while `language` is "hi". Reading `resolvedLanguage` first would
  // leave the dropdown label stuck on "English" AND make the
  // `if (lng === current) return` guard swallow English clicks — the exact
  // production regression reported by the user.
  const readLang = () => (i18n.language || i18n.resolvedLanguage || "en").split("-")[0];
  const [current, setCurrent] = useState(readLang);
  useEffect(() => {
    const onChange = () => setCurrent(readLang());
    i18n.on("languageChanged", onChange);
    // Re-read once on mount in case an async syncLangFromProfile completed
    // between component mount and this effect subscribing.
    onChange();
    return () => i18n.off("languageChanged", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n]);

  const change = async (lng) => {
    if (lng === current) return;
    // R109.32 — Hindi translations live in a lazy chunk. Load them FIRST so
    // the very first render after the language switch already has Hindi
    // resources — no English fallback flash.
    if (lng === "hi") {
      try { await ensureHindiLoaded(); } catch (_) {}
    }
    await i18n.changeLanguage(lng);
    // R109.33 — remember that THIS user explicitly picked a language so
    // subsequent bootstraps do NOT clobber the choice with a stale profile read.
    try {
      localStorage.setItem(LS_LANG_KEY, lng);
      localStorage.setItem(LS_LANG_USER_SET, "1");
    } catch (_) {}
    // Persist to backend profile when authenticated. AWAIT the write so the
    // next reload's GET /profile always sees the fresh value — otherwise a
    // race would flip the UI back on the next bootstrap.
    if (user) {
      try {
        const token = localStorage.getItem("venturekhoj_token");
        await fetch(`${API_BASE}/profile/language`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify({ language: lng }),
        });
        if (refresh) refresh();
      } catch (_) {}
    }
  };

  const btnClass =
    variant === "light"
      ? "rounded-full text-white hover:bg-white/10 hover:text-white h-9 px-3"
      : "rounded-full text-slate-900 hover:bg-slate-100 h-9 px-3";

  // Compact segmented EN | हिं toggle — both options always visible (used on
  // the mobile top bar so language is switchable without opening any menu).
  if (variant === "compact") {
    return (
      <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-0.5" data-testid="lang-switcher-compact">
        {SUPPORTED_LANGS.map((lng) => (
          <button
            key={lng}
            type="button"
            data-testid={`lang-option-${lng}`}
            onClick={() => change(lng)}
            aria-pressed={lng === current}
            className={`rounded-full px-2.5 py-1 text-xs font-bold transition ${
              lng === current ? "bg-emerald-500 text-slate-900 shadow-sm" : "text-slate-600"
            }`}
          >
            {lng === "hi" ? "हिं" : "EN"}
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={btnClass}
          data-testid="lang-switcher-btn"
          aria-label="Change language"
        >
          <Languages className="mr-1.5 h-4 w-4" />
          <span className="text-sm font-semibold">{LABELS[current] || "English"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40" data-testid="lang-switcher-menu">
        {SUPPORTED_LANGS.map((lng) => (
          <DropdownMenuItem
            key={lng}
            data-testid={`lang-option-${lng}`}
            onClick={() => change(lng)}
            className="cursor-pointer flex items-center justify-between"
          >
            <span>{LABELS[lng]}</span>
            {lng === current && <Check className="h-4 w-4 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
